/* eslint-disable max-len */
import Fuse from 'fuse.js';
// eslint-disable-next-line import/order
import { createSelector } from 'reselect';
import {
  DevicePreviewBuilder,
  PROMO_TOOLTIP_BUTTON_TEXT,
  getFormattedDeliveryDate,
} from '@beef/shop-common';

import { getBasketProductIds } from 'store/common/basket/selectors';
import { getComparedProductIds } from 'store/common/compare/selectors';
import * as Product from 'store/common/constants/product';
import {
  getCurrentRegionId,
  getStoreCookiePath,
  getValueOfCookie,
  isTradeInEnabled,
} from 'store/common/settings/selectors';
import { getBodyIsTariffFlag } from 'store/common/tariffs/selectors';
import { getTradeInSettings } from 'store/common/tradeIn/selectors';
import { REQUEST_INPROGRESS } from 'store/constants';
import { DeviceInstallmentCount } from 'store/ProductCatalog/products/constants';
import { getTradeInEstimate } from 'store/widgets/TradeInCalculator/selectors';
import { convertListToObjectBy } from 'utils/fn';
import { formatPrice } from 'utils/formatting';
import getLocalCurrencyStr from 'utils/getLocalCurrencyStr';
import { removeArrayDuplicates } from 'utils/remove-duplicates';

import { getBodyData } from '../selectors';
import {
  CONTENT_TAB_DELIVERY,
  DETAILS_VIEW_OUT_OF_STOCK,
  DETAILS_VIEW_PREORDER,
  DETAILS_VIEW_SIMPLE,
  DETAILS_VIEW_TARIFF,
  SERVICE_TYPE_INSURANCE,
  TabEnum,
} from './constants';
import { getDeliveryOfficesListSortFunc } from './delivery/selectors';
import { FEEDBACK_FETCH_LIMIT } from './feedbackLocal/constants';
import { unpackNewRating } from './feedbackLocal/helpers';

// import { createReduceSelector } from '../../utils/redux'
// import { getData } from '../ProductCatalog/products/selectors'

/**
 * User controlled local store data
 */
const HAS_SERVICE_PROMOTION_ID = 110;
const getDetailsPageData = getBodyData;
const getDetailsCommonData = (state) => getDetailsPageData(state).common;
const getDetailsFeedbackData = (state) => getDetailsPageData(state).feedbackLocal;
const getDetailsServicesData = (state) => getDetailsPageData(state).servicesLocal;
export const getProductImpression = (state) => getDetailsPageData(state).impression || {};
const getDetailsPromotion = (state) => getDetailsPageData(state).promotion;

/**
 * -----------------
 * Feature selectors
 * -----------------
 * */

/**
 * DevicePlus
 */
export const getDetailsDevicePlusInfo = (state) => getDetailsPageData(state).bonusService || {};
export const isDetailsDeviceBestPricePromotionSelector = (state) =>
  getDetailsPageData(state).isBestPricePromotion;
export const getDetailsDevicePlusHTMLBlock = (state) =>
  getDetailsDevicePlusInfo(state).contentPromotionText;
export const getDetailsDevicePlusFinalHeader = (state) =>
  getDetailsDevicePlusInfo(state).finalHeader || null;
export const getDetailsDevicePlusFinalText = (state) =>
  getDetailsDevicePlusInfo(state).finalText || null;

/** page types */
export const getDetailsViewSlug = (state) => {
  if (getBodyData(state).isOutOfStock) {
    return DETAILS_VIEW_OUT_OF_STOCK;
  }

  if (getBodyData(state).isPreorder) {
    return DETAILS_VIEW_PREORDER;
  }

  if (getBodyData(state).isTariff) {
    return DETAILS_VIEW_TARIFF;
  }

  return DETAILS_VIEW_SIMPLE;
};

/**
 * Tariff details selectors
 */
export const getDetailsIsTariffFlag = getBodyIsTariffFlag;
export const getDetailsTariffDpcId = (state) => getBodyData(state).dpcId || null;
/**
 * layout
 */
export const getGalleryImages = (state) => getBodyData(state).images;
export const getFirstGalleryImage = (state) => getBodyData(state).images[0] || null;

/* for SEO purposes */
export const getFirstGalleryImageThumbUrl = (state) =>
  (getFirstGalleryImage(state) || {}).thumbUrl || '';
export const getSeoMetaData = (state) => getBodyData(state).metadata || {};
export const getSeoMetaDescription = (state) => getSeoMetaData(state).descriptionSeo || '';

export const getContentTabsRaw = (state) => getBodyData(state).contentTabs;

export const getContentTabsListFiltered = createSelector(
  getContentTabsRaw,
  getDetailsPromotion,
  (list, promotion) => {
    const hasService = (promotion?.available?.extraProducts || []).filter(
      (item) => item.kind === HAS_SERVICE_PROMOTION_ID,
    );
    const filteredList = (list || []).filter((item) => item.slug !== TabEnum.CONTENT_TAB_SERVICES);
    return hasService.length !== 0 ? filteredList : list;
  },
);
export const getContentTabsList = createSelector(getContentTabsListFiltered, (list) =>
  list.map((tab) => ({
    id: tab.slug,
    title: tab.name,
  })),
);

export const getContentTabsHash = createSelector(getContentTabsListFiltered, (list) =>
  list.reduce((acc, cur) => {
    acc[cur.slug] = cur;
    return acc;
  }, {}),
);

const getContentTabsDict = createSelector(
  getContentTabsListFiltered,
  convertListToObjectBy('slug'),
);
export const getLoadedActiveTab = (state) => getBodyData(state).activeContentTab; // initially active content tab
const getFeedbackTabInfo = (state) => getContentTabsDict(state)[TabEnum.CONTENT_TAB_FEEDBACK] || {};
const getPaymentTabInfo = (state) => getContentTabsDict(state)[TabEnum.CONTENT_TAB_PAYMENT] || {};
const getLeasingTabInfo = (state) => getContentTabsDict(state)[TabEnum.CONTENT_TAB_LEASING] || {};
const getDeliveryTabInfo = (state) => getContentTabsDict(state)[TabEnum.CONTENT_TAB_DELIVERY] || {};
const getPromoTabInfo = (state) => getContentTabsDict(state)[TabEnum.CONTENT_TAB_PROMO] || {};

export const getFeedbackTabAvailability = (state) => !!getFeedbackTabInfo(state);
/**
 * product remains
 */
export const getProductRemains = (state) => getBodyData(state).productRemains;
/**
 * product
 */
export const getProductId = (state) => getBodyData(state).productId;
export const getProductIdWithRegion = createSelector(
  getProductId,
  getCurrentRegionId,
  (productId, currentRegion) => `${currentRegion}_${productId}`,
);
export const getPromotionId = (state) => getBodyData(state).promotion?.available?.id;
export const getProductArticle = (state) => getBodyData(state).article;
export const getBrandName = (state) => getBodyData(state).brandName;
export const getMainCollectionId = (state) => getBodyData(state).mainCollectionId;
export const getMainCollectionName = (state) => getBodyData(state).mainCollectionName;
export const getSubCollectionId = (state) => getBodyData(state).subCollectionId;
export const getProductName = (state) => getBodyData(state).productName;
export const getProductSlug = (state) => getBodyData(state).productSlug;
export const getProductIsPreorder = (state) => getBodyData(state).isPreorder;
export const getIsOutOfStock = (state) => getBodyData(state).isOutOfStock;

/**
 * forcePromotion
 */
const getForcePromotionData = (state) => getBodyData(state).forcePromotion || {};
// export const getForcePromotionAvailability = state => !!getForcePromotionData(state).forcePromotion;
export const getForcePromotionId = (state) => getForcePromotionData(state).id || null;
export const getForcePromotionSlug = (state) => getForcePromotionData(state).slug || null;

/**
 * product characteristics
 */
const getCharacteristicsData = (state) => getBodyData(state).characteristics;
export const getBriefCharacteristics = (state) => getCharacteristicsData(state).brief;
export const getDetailedCharacteristics = (state) => getCharacteristicsData(state).detailed;
export const getBriefCharacteristicsLeft = createSelector(getBriefCharacteristics, (brief) => {
  const briefLeftCur = [];
  if (brief && brief.length !== 0) {
    for (let i = 0; i < brief.length; i += 1) {
      if (i % 2 === 0) {
        briefLeftCur.push(brief[i]);
      }
    }
  }
  return briefLeftCur;
});
export const getBriefCharacteristicsRight = createSelector(getBriefCharacteristics, (brief) => {
  const briefRightCur = [];
  if (brief && brief.length !== 0) {
    for (let i = 0; i < brief.length; i += 1) {
      if (i % 2 !== 0) {
        briefRightCur.push(brief[i]);
      }
    }
  }
  return briefRightCur;
});
export const getDetailedSplitted = createSelector(getDetailedCharacteristics, (detailed) => {
  const detailedSplittedCar = [];

  if (detailed && detailed.length !== 0) {
    for (let i = 0; i < detailed.length; i += 1) {
      const detailedLeftCur = [];
      const detailedRightCur = [];
      for (let j = 0; j < detailed[i]?.list?.length; j += 1) {
        if (j % 2 === 0) {
          detailedLeftCur.push(detailed[i]?.list[j]);
        } else {
          detailedRightCur.push(detailed[i]?.list[j]);
        }
      }

      detailedSplittedCar.push({
        group: detailed[i].group,
        list: { left: detailedLeftCur, right: detailedRightCur },
      });
    }
  }
  return detailedSplittedCar;
});
/**
 * product descriptions
 */

export const getDescription = (state) => getBodyData(state).description;

/**
 * price
 */
const getPriceData = (state) => getBodyData(state).price || {};

export const getPrice = (state) => getPriceData(state).value;
export const getOldPrice = (state) => getPriceData(state)?.oldValue;
export const getPriceDiscount = (state) => getPriceData(state)?.discountPercent;

/**
 * content promotion
 */
export const getContentPromotion = (state) => getBodyData(state).contentPromotion;

/**
 * feedback
 */
const getFeedbackData = (state) => getBodyData(state).feedback;

/**
 * esim
 */

export const getEsimData = (state) => getBodyData(state).esim || {};

export const getEsimAvailable = (state) => getEsimData(state).hasESim || false;
export const getEsimBannerMarkup = (state) => getEsimData(state).bannerMarkup || '';

export const getDetailsOfficeBuyInfo = (state) => getBodyData(state).officeBuy || {};
export const getDetailsESimInfo = (state) => getDetailsOfficeBuyInfo(state).esim || {};

export const getDetailsESimSPButton = (state) =>
  getDetailsESimInfo(state).thank_you_button_text || '';
export const getDetailsESimSPUrl = (state) => getDetailsESimInfo(state).thank_you_button_url || '';
export const getDetailsESimSPText = (state) => getDetailsESimInfo(state).thank_you_message || '';

/**
 * promo banner markup
 */

export const getMnpBannerMarkup = (state) => getBodyData(state).promoBannerMarkup;

// в авторизованной зоне показываем баннер esim, баннер mnp не показываем
export const getPromoBannerShowAuthorized = createSelector(
  getEsimAvailable,
  (hasEsim) => !!hasEsim,
);

export const getPromoBannerMarkup = createSelector(
  getEsimAvailable,
  getEsimBannerMarkup,
  getMnpBannerMarkup,
  (hasEsim, esimMarkup, mnpMarkup) => {
    if (hasEsim) {
      return esimMarkup;
    }
    return mnpMarkup;
  },
);

// TODO: add fetchLimit to backend
export const getFeedbackFetchLimit = (state) =>
  getFeedbackData(state).fetchLimit || FEEDBACK_FETCH_LIMIT;
export const getFeedbackRating = (state) => getFeedbackData(state).average;
export const getFeedbackPostsInitial = (state) => getFeedbackData(state).list || []; // only initially loaded posts
export const getFeedbackCount = (state) => getFeedbackData(state).count; // total feedback count
export const getFeedbackPageNumber = (state) => getDetailsFeedbackData(state).pageNumber; // current feedbacks page number
export const getFeedbackAvailability = (state) => !!getFeedbackCount(state);
export const getFeedbackSubmitUrl = (state) => getFeedbackData(state).submitUrl;
export const getFeedbackChangeUsefulnessUrl = (state) => getFeedbackData(state).changeUsefulnessUrl; // url to [dis]like
export const getFeedbackLoadMoreUrl = (state) => getFeedbackData(state).loadMoreUrl; // url to fetch more comments

// name of cookie for username used to comment on the product
export const getFeedbackCommentMaxLength = (state) =>
  getFeedbackData(state).maxCommentLength || 2000;
export const getFeedbackCookiePath = getStoreCookiePath;
export const getFeedbackRatedCookieName = (state) => getFeedbackData(state).ratedCookieName;
export const getFeedbackNewRatedCookieName = (state) => getFeedbackData(state).newRatedCookieName;
export const getFeedbackMostUsefulPost = (state) => getFeedbackData(state).mostUseful;
export const getFeedbackAgreement = (state) => getFeedbackData(state).agreement;

/** local feedback */
export const getFeedbackFormData = createSelector(
  getDetailsFeedbackData,
  ({ Comment, Email, Name, Rating }) => ({
    Comment,
    Email,
    Name,
    Rating,
  }),
);
export const getFeedbackUserName = (state) => getDetailsFeedbackData(state).Name;
export const getFeedbackUserEmail = (state) => getDetailsFeedbackData(state).Email;
export const getFeedbackUserComment = (state) => getDetailsFeedbackData(state).Comment;
export const getFeedbackUserRating = (state) => getDetailsFeedbackData(state).Rating;
export const getFeedbackSubmitStatus = (state) => getDetailsFeedbackData(state).status;
export const getFeedbackPostsFetched = (state) => getDetailsFeedbackData(state).fetchedList || []; // only fetched list

export const getFeedbackSuccessSubmitTemplate = (state) =>
  getFeedbackData(state).submitSuccessTemplate;
export const getFeedbackSuccessMessage = createSelector(
  getFeedbackSuccessSubmitTemplate,
  getFeedbackUserName,
  (template, userName) => template.replace(/{{name}}/, userName),
);
export const getFeedbackErrorSubmitTemplate = (state) => getFeedbackData(state).submitErrorTemplate;
export const getFeedbackCommonErrorMessage = createSelector(
  getFeedbackErrorSubmitTemplate,
  getFeedbackUserName,
  (template, userName) => template.replace(/{{name}}/, userName),
);

/** returns specific error message if passed to error callback, or common message if none were passed */
export const getFeedbackErrorMessage = (state) =>
  getDetailsFeedbackData(state).errorMessage || getFeedbackCommonErrorMessage(state);

export const getFeedbackErrorDetails = (state) => getDetailsFeedbackData(state).errors;

// full list of feedbacks initial + loaded
export const getFeedbackPosts = createSelector(
  getFeedbackPostsInitial,
  getFeedbackPostsFetched,
  (initial, fetched) => initial.concat(fetched),
);

// count of feedback still not fetched
export const getFeedbackCurrentCount = (state) => getFeedbackPosts(state).length;
export const getFeedbackMoreCount = (state) =>
  getFeedbackData(state).count - getFeedbackCurrentCount(state);
// count of feedbacks that will be fetched on the next request
export const getFeedbackFetchCount = (state) =>
  Math.min(getFeedbackMoreCount(state), getFeedbackFetchLimit(state));
export const getFeedbackSortBy = (state) => getFeedbackData(state).sortBy;
export const getFeedbackIsLoading = (state) =>
  getDetailsFeedbackData(state).isFetchInProgress || false;

/**
 * retrieves list of rated ids from cookies
 * @deprecated this selector is only used to maintain compatibility with deprecated product Card
 */
const getLikedFeedbacksIds = createSelector(
  getValueOfCookie(getFeedbackRatedCookieName),
  (ratedCookie) => {
    const [, feedbackIds = ''] = (ratedCookie || '').split('|');
    return feedbackIds.split(',').map((number) => parseInt(number, 10));
  },
);

/**
 * retrieves list of positively and negatively rated ids from cookies
 */
export const getFeedbackNewRating = createSelector(
  getValueOfCookie(getFeedbackNewRatedCookieName),
  (newRatedCookie) => unpackNewRating(newRatedCookie),
);

/**
 * extends post item with liked flag
 * isLiked shows that the post was positively liked
 * isDisliked shows that post was negatively liked
 * isRated apart from isLiked and isDisliked, shows posts that were rated before
 * liked, disliked and oldRated are lists of matching ids
 */
const enrichFeedbackPost =
  ({ disliked, liked }, oldRated, fallbackTitle) =>
  (item) => {
    const isLiked = liked.indexOf(item.id) !== -1;
    const isDisliked = disliked.indexOf(item.id) !== -1;
    const isRated = oldRated.indexOf(item.id) !== -1;
    return {
      ...item,
      dislikes: item.dislikes + (isDisliked ? 1 : 0),
      isDisliked,
      isLiked,
      isRated: isLiked || isDisliked || isRated,
      likes: item.likes + (isLiked ? 1 : 0),
      // TODO: fix this crutch somehow, by providing a custom product title or just
      title: item.title || fallbackTitle,
    };
  };

/**
 * applies enrichFeedbackPost to a list of posts
 */
export const getFeedbackPostsAdvanced = createSelector(
  getFeedbackPosts,
  getFeedbackNewRating,
  getLikedFeedbacksIds,
  getProductName,
  (list, { disliked, liked }, oldRated, productName) =>
    list.map(enrichFeedbackPost({ disliked, liked }, oldRated, `Отзыв о ${productName}`)),
);

/**
 * applies enrichFeedbackPost to most useful post item
 */
export const getFeedbackMostUsefulPostAdvanced = createSelector(
  getFeedbackMostUsefulPost,
  getFeedbackNewRating,
  getLikedFeedbacksIds,
  (mostUsefulPost, { disliked, liked }, oldRated) => {
    /** only do enrichment if post is not falsy */
    if (mostUsefulPost) return enrichFeedbackPost({ disliked, liked }, oldRated)(mostUsefulPost);
    return mostUsefulPost;
  },
);

/**
 * urls
 */

export const getCatalogProductsRecentlyViewedProducts = (state) => state.recentlyViewedProducts;
export const getBaseUrl = (state) => getBodyData(state).baseUrl;
export const getRequestUrl = (state) => getBodyData(state).requestUrl;
export const getAbsoluteBaseUrl = (state) => getBodyData(state).absoluteBaseUrl;

export const getFeedbackTabUrl = (state) => getFeedbackTabInfo(state).url;
export const getPaymentTabUrl = (state) => getPaymentTabInfo(state).url;
export const getLeasingTabUrl = (state) => getLeasingTabInfo(state).url;
export const getDeliveryTabUrl = (state) => getDeliveryTabInfo(state).url;
export const getDeliveryCourierUrl = (state) => getDeliveryCourierData(state).url;
export const getPromoTabUrl = (state) => getPromoTabInfo(state).url;

/**
 * paymentDetails
 *
 */
const getPaymentDetails = (state) => getBodyData(state).paymentDetails || {};
export const getPaymentHeadline = (state) => getPaymentDetails(state).headline;
export const getPaymentContent = (state) => getPaymentDetails(state).content;

/**
 * deliveryDetails
 */
export const getDeliveryData = (state) => (getBodyData(state) || {}).deliveryDetails || {};
export const getDeliveryHeadline = (state) => getDeliveryData(state).headline || '';
export const getDeliveryTakeawayData = (state) => getDeliveryData(state).takeaway || null;

export const getDeliveryTakeawayAvailability = (state) => !!getDeliveryTakeawayData(state);
export const getDeliveryOfficesAvailability = createSelector(
  getDeliveryTakeawayData,
  (takeawayData) => !!(takeawayData || {}).offices,
);

export const getDeliveryCourierData = (state) => getDeliveryData(state).courier;
export const getDeliveryPickupBuyData = (state) => getDeliveryData(state).pickupBuy;
export const getDeliveryPickupBuyOpenState = (state) =>
  getDetailsCommonData(state).deliveryPickupOpenState;
export const getDeliveryPickupBuyPoint = (state) => getDetailsCommonData(state).deliveryPickupPoint;
export const getDeliveryPickupBuyViewState = (state) =>
  getDetailsCommonData(state).deliveryPickupViewState;
export const getDeliveryPickupBuySuccess = (state) =>
  getDetailsCommonData(state).deliveryPickupSuccess;
export const getDeliveryPickupBuyLoading = (state) =>
  getDetailsCommonData(state).deliveryPickupLoading;
export const getDeliveryPickupBuyErrorDetails = (state) =>
  getDetailsCommonData(state).deliveryPickupErrorDetails;
export const selectIsBundleSwitched = (state) => getDetailsCommonData(state).isBundleSwitched;
export const selectIsBundleActive = (state) =>
  selectIsBundleSwitched(state) && selectIsPromotionAvailable(state);

export const getDeliveryTypes = createSelector(
  getDeliveryTakeawayData,
  getDeliveryCourierData,
  (takeaway, courier) =>
    [].concat(
      takeaway ?
        [
          {
            ...takeaway,
            slug: 'takeaway',
          },
        ]
      : [],
      courier ?
        [
          {
            ...courier,
            slug: 'courier',
          },
        ]
      : [],
    ),
);

export const getDeliveryTakeawayViews = (state) =>
  getDeliveryTakeawayAvailability(state) ? getDeliveryTakeawayData(state).views : [];
export const getDeliveryOfficesListWithDuplicates = (state) =>
  getDeliveryOfficesAvailability(state) ? getDeliveryTakeawayData(state).offices : [];

export const getDeliveryOfficesDataUrl = (state) => getDeliveryTakeawayData(state).dataUrl || '';
export const getDeliveryOfficesDataFilter = (state) =>
  getDeliveryTakeawayData(state).dataFilter || '';

/**
 * HotFix SHOP-2945 duplicate offices
 * TODO: remove this selector from chain once we move to GraphQL
 */
export const getDeliveryOfficesListRaw = createSelector(
  getDeliveryOfficesListWithDuplicates,
  (list) => removeArrayDuplicates(list, (a, b) => a.pointCode === b.pointCode),
);

/** Filters for delivery takeaway */
export const getDeliveryTakeawaySettings = (state) =>
  (getDeliveryTakeawayData(state) || {}).settings || {};

const getDeliveryTakeawayAllOffices = (state) => getDeliveryData(state).allOffices || [];
export const getDeliveryTakeawayPins = (state) => getDeliveryTakeawaySettings(state).points || {};

export const getDeliveryTakeawayFilters = (state) =>
  getDeliveryTakeawayAvailability(state) ? getDeliveryTakeawaySettings(state).filters : [];

export const getDeliveryTakeawayActiveFilters = createSelector(
  getDeliveryTakeawayFilters,
  (filters) => filters.filter((item) => item.checked).map((item) => item.slug),
);

export const getDeliveryTakeawayFilterHeaders = (state) =>
  getDeliveryTakeawayAvailability(state) ? getDeliveryTakeawaySettings(state).headers : [];

export const getDeliveryTakeawayFiltersToShow = createSelector(
  getDeliveryTakeawayAllOffices,
  getDeliveryTakeawayFilters,
  (list, filters) => {
    const firstUniqueByTypeOffices = list.filter(
      (
        (set) => (office) =>
          !set.has(office.ptype) && set.add(office.ptype)
      )(new Set()),
    );
    const filtersToShow = firstUniqueByTypeOffices.map((office) => {
      const filter = filters.find((filt) => office.ptype === filt.slug);
      const newFilter = { ...filter };
      newFilter.title = newFilter.title.replace(`pr_${office.ptype}`, office.price);
      return newFilter;
    });
    return filtersToShow.length > 1 ? filtersToShow : [];
  },
);

const getAvailableDate = (office) =>
  getFormattedDeliveryDate(office.delivery_days || [], office.movement_id, office.equipment_id);

export const getDeliveryTakeawayFilteredOffices = createSelector(
  getDeliveryTakeawayAllOffices,
  getDeliveryTakeawayActiveFilters,
  getDeliveryTakeawayFiltersToShow,
  (offices, activeFilters, filtersToShow) => {
    const mappedOffices = offices.map((office) => ({
      ...office,
      availableDate: getAvailableDate(office),
    }));
    return filtersToShow.length !== 0 ?
        mappedOffices.filter((office) => activeFilters.includes(office.ptype))
      : mappedOffices;
  },
);

// TODO: delete his selector if the only purpose of it is to get takeaway availability
export const getDeliveryOfficesCount = (state) => getDeliveryOfficesListRaw(state).length;
export const getDeliveryHasOffices = (state) =>
  getDeliveryOfficesListWithDuplicates(state).length > 0;
export const getDeliveryTakeawaySearchQuery = (state) =>
  getDetailsCommonData(state).deliveryTakeawaySearchQuery || '';

/** to make fuse work better, we trim query string */
export const getDeliveryTakeawaySearchQueryTrimmed = (state) =>
  getDeliveryTakeawaySearchQuery(state).replace(/^\s*(.*?)\s*$/, '$1');

const getDeliveryOfficesFused = createSelector(
  getDeliveryTakeawayFilteredOffices,
  (list) =>
    new Fuse(list, {
      distance: 300,
      includeScore: true,
      keys: ['address', 'subway.name'],
      location: 0,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      shouldSort: true,
      threshold: 0.4,
      tokenize: false,
    }),
);

/**
 * Using Fuse, selector finds the best matches, returns empty list when query is empty
 */
export const getDeliveryOfficesListUnsorted = createSelector(
  getDeliveryOfficesFused,
  getDeliveryTakeawayFilteredOffices,
  getDeliveryTakeawaySearchQueryTrimmed,
  (listFused, listRaw, query) => {
    if (query) {
      const list = listFused.search(query);
      return list.map(({ item }) => item);
    }

    return listRaw;
  },
);

/**
 * Using current state of sort widget for compare function
 */

export const getDeliveryOfficesList = createSelector(
  getDeliveryOfficesListUnsorted,
  getDeliveryOfficesListSortFunc,
  (listUnsorted, sortFunc) => (sortFunc ? listUnsorted.slice().sort(sortFunc) : listUnsorted),
);

export const getDeliveryFilteredOfficesCount = (state) => getDeliveryOfficesList(state).length;

/**
 * services
 */
export const getServices = (state) => getBodyData(state).services || [];
export const getActiveServiceIds = (state) => getDetailsServicesData(state).activeServiceIds || [];
export const getActiveServicesBasket = createSelector(getActiveServiceIds, (activeIds) =>
  activeIds.map((id) => ({
    id,
    quantity: 1,
  })),
);

/**
 * Extends services, by adding extra isActive flag maintained on client-side
 */
export const getFullServices = createSelector(getServices, getActiveServiceIds, (list, activeIds) =>
  list.map((service) => ({
    ...service,
    isActive: activeIds.indexOf(service.id) !== -1,
  })),
);

export const getFullInsuranceServices = (state) =>
  getFullServices(state).filter(({ type }) => type === SERVICE_TYPE_INSURANCE);
export const getInsuranceAvailability = (state) => !!getFullInsuranceServices(state).length;
export const getHasInsurance = (state) =>
  !!getFullInsuranceServices(state).filter(({ isActive }) => isActive).length;

/**
 * credit
 */
const getCreditData = (state) => getBodyData(state).creditDetails;
const getInstallmentData = (state) => getCreditData(state).installment;

export const getInstallmentCallToAction = (state) => getInstallmentData(state).callToAction;
export const getCreditSlug = (state) => getCreditData(state).slug;

/** TODO: remove hardcode for empty slug, when SHOP- is done */
export const getCreditAvailability = (state) =>
  getCreditData(state).availableCredit && !!getCreditSlug(state);

/** following fields vary based on insurance */
export const getCreditTitle = (state) =>
  (getHasInsurance(state) ? getInstallmentData(state) : getCreditData(state)).title;
export const getCreditButtonText = (state) =>
  (getHasInsurance(state) ? getInstallmentData(state) : getCreditData(state)).buttonText;

export const getCreditMinPayment = (state) => getCreditData(state).minPayment;

export const getCreditMinPaymentAvailability = (state) => !!getCreditMinPayment(state);
export const getCreditReferenceTemplate = (state) => getCreditData(state).referenceTemplate;

/**
 * leasing (samsung forward)
 */
const getLeasingData = (state) => getBodyData(state).leasingDetails || {};

export const getLeasingAvailability = (state) => !!getBodyData(state).leasingDetails;
export const getLeasingMinPayment = (state) => getLeasingData(state).minPayment;

export const getLeasingMinPaymentAvailability = (state) => !!getLeasingMinPayment(state);
export const getLeasingReferenceTemplate = (state) => getLeasingData(state).referenceTemplate || '';

/** leasing for bundle */
export const getLeasingPromotionData = (state) => getLeasingData(state).promotion || {};
export const getLeasingBundleAvailability = (state) =>
  !!getLeasingPromotionData(state).leasingProductCode;
export const getLeasingMonthPayment = (state) => getLeasingPromotionData(state).monthPayment;

/** @deprecated please remove after migrating to specific selectors */
export const getCreditDetailsDeprecated = createSelector(
  getInstallmentCallToAction,
  getCreditSlug,
  getCreditTitle,
  getCreditButtonText,
  getCreditMinPayment,
  (label, slug, headline, buttonText, value) => ({
    buttonText,
    headline,
    label,
    slug,
    value,
  }),
);

/** common */
export const getActiveContentTabSlug = (state) =>
  getDetailsCommonData(state).currentSection || getLoadedActiveTab(state);
/** returns data within currently active content tab, or an empty object */
export const getActiveContentTabData = (state) =>
  getContentTabsDict(state)[getActiveContentTabSlug(state)] || {};
export const getActiveDeliverySection = (state) =>
  getDetailsCommonData(state).currentDeliverySection;

export const getActiveContentTabProductTitle = (state) =>
  getActiveContentTabData(state).productTitleOptions || {};
export const getActiveContentTabMetaTitle = (state) =>
  getActiveContentTabData(state).metaTitle || null;

/**
 * Page title will be fetched from currently active content tab if it is set,
 * otherwise the global title will be used
 */
export const getActiveTabSimplifiedName = createSelector(getActiveContentTabSlug, (activeSlug) => {
  const contentSubstr = 'content-';
  return activeSlug.includes(contentSubstr) ? activeSlug.replace(contentSubstr, '') : activeSlug;
});
export const getTabTitle = createSelector(
  getActiveTabSimplifiedName,
  getActiveContentTabProductTitle,
  getActiveDeliverySection,
  (activeSlug, titles, deliveryActiveSlug) =>
    activeSlug === CONTENT_TAB_DELIVERY ? titles[deliveryActiveSlug] : titles[activeSlug],
);

export const getPageTitle = (state) => getTabTitle(state) || getBodyData(state).title;

export const getPageMetaTitle = (state) =>
  getActiveContentTabMetaTitle(state) || getBodyData(state).metaTitle;

/** compare */
export const isProductCompared = createSelector(
  getComparedProductIds,
  getProductId,
  (comparedProducts, productId) => comparedProducts.indexOf(productId) !== -1,
);

/** colors */
export const getColors = (state) => getBodyData(state).colors || [];
export const getActiveColor = (state) => getBodyData(state).activeColor || null;

/** types */
export const getTypesParams = (state) => getBodyData(state).types || [];

/** getFancyNumbersList */
export const getFancyNumbersStatus = (state) => getDetailsCommonData(state).fancyNumbersStatus;
export const getFancyNumbersList = (state) => getDetailsCommonData(state).fancyNumbers;
export const getFancyNumbersReady = (state) => !!getFancyNumbersList(state).length;
export const getFancyNumbersLoading = (state) =>
  getFancyNumbersStatus(state) === REQUEST_INPROGRESS;
export const getFancyNumbersActiveIndex = (state) => getDetailsCommonData(state).fancyNumbersIndex;
export const getActiveFancyNumber = createSelector(
  getFancyNumbersList,
  getFancyNumbersActiveIndex,
  (numbers, index) => numbers[index],
);

/** badges */
export const getBadges = (state) => getBodyData(state).badges || [];
export const getBadgesList = createSelector(getBadges, (badges) =>
  badges.map((item) => item.title),
);
export const getBadgesTitlesString = createSelector(getBadgesList, (badgeTitles) =>
  badgeTitles.reduce((prevString, title) => (prevString ? `${prevString}|${title}` : title), ''),
);

/** highlight badges */
export const getHighlightBadges = (state) => getBodyData(state).highlightBadges || [];

/** tariff parameters */
export const getDetailsTariffParameters = (state) => getDetailsPageData(state).tariffParams || [];

/** shopCampaings */
export const getDetailsCampaigns = (state) => getDetailsPageData(state).shopCampaigns || [];
export const getDetailsIsAuthorized = (state) => getDetailsPageData(state).isAuthorized || false;
export const getDetailsHasCampaigns = createSelector(
  getDetailsCampaigns,
  (campaigns) => !!campaigns.length,
);
export const getDetailsCampaignsContent = createSelector(getDetailsCampaigns, (campaigns) => {
  return (campaigns[0] || {}).description || '';
});

/** promotion */
const getPromotionData = (state) => getBodyData(state).promotion || {};
export const selectIsPromotionAvailable = (state) => !!getPromotionData(state).available;
export const getPromotionAvailable = (state) => getPromotionData(state).available || {};
const getPromotionDeliveryMethods = (state) => getPromotionAvailable(state).deliveryMethods || [];
export const getPromotionExtraProducts = (state) =>
  DevicePreviewBuilder.composeExtraProducts(getPromotionAvailable(state).extraProducts);
export const selectPromotionOldPrice = (state) =>
  getPromotionAvailable(state).priceWithoutPromotion;
export const selectPromotionPrice = (state) => getPromotionAvailable(state).bundlePrice;
export const selectPromotionDiscount = (state) => getPromotionAvailable(state).discountPercent;

/** delivery benefits */
const getProductDelivery = (state) => getBodyData(state).delivery || {};
const useProductDeliveryMethods = (state) => getProductDelivery(state).useMethods || false;
const getProductDeliveryMethods = (state) => getProductDelivery(state).methods || [];
const getProductDeliveryMethodsList = createSelector(
  selectIsPromotionAvailable,
  getPromotionDeliveryMethods,
  getProductDeliveryMethods,
  (isBundle, bundleMethods, productMethods) => (isBundle ? bundleMethods : productMethods),
);
const getProductDeliveryMethodsDict = createSelector(
  getProductDeliveryMethodsList,
  convertListToObjectBy('type'),
);

export const getProductDeliveryBenefits = createSelector(
  useProductDeliveryMethods,
  getProductDeliveryMethodsDict,
  (useMethods, methods) => {
    let deliveryBenefits = [];
    if ((methods || []).length === 0 || !useMethods) return [];

    if (methods.pickup || methods.movements) {
      deliveryBenefits = [...deliveryBenefits, { slug: Product.PICKUP }];
    }

    if (methods.fast_delivery) {
      const deliveryTwoHours = {
        endTime: methods.fast_delivery.endTime,
        slug: Product.DELIVERY_TWO_HOURS,
        startTime: methods.fast_delivery.startTime,
      };
      if (methods.fast_delivery.delay) {
        deliveryBenefits = [...deliveryBenefits, deliveryTwoHours];
      }
    } else if (methods.courier) {
      const courierDelivery = {
        endTime: methods.courier.endTime,
        slug: methods.courier.price > 0 ? Product.DELIVERY : Product.FREE_DELIVERY,
        startTime: methods.courier.startTime,
      };

      deliveryBenefits = [...deliveryBenefits, courierDelivery];
    }
    return deliveryBenefits;
  },
);

export const getFastDeliveryDelay = (state) => {
  const fastDeliveryMethods = state.body.delivery.methods.filter(
    (cur) => cur.type === 'fast_delivery',
  );
  return fastDeliveryMethods.length !== 0 ? fastDeliveryMethods[0].delay : null;
};

/**
 * Sale of product is near price
 */
export const getProductSale = (state) => getBodyData(state).showSaleDetails || {};
export const getProductSaleAvailability = (state) => !!getBodyData(state).showSaleDetails;
export const getProductSaleImage = (state) => getProductSale(state).showSaleImage;
export const getProductSaleText = (state) => getProductSale(state).showSaleText;

export const getDetailsPartnerSlug = (state) => getDetailsPageData(state).partnerSlug || '';

export const promoBundleSelector = (state) => {
  const available = getPromotionAvailable(state);
  const url = getBaseUrl(state);

  const promoBundle = {
    available: !!available?.name,
    label: available?.name,
    link: `${url}#promo`,
  };

  return promoBundle.available && promoBundle;
};

export const contentPromotionSelector = (state) => {
  const contentPromotionText = getContentPromotion(state);
  const url = getBaseUrl(state);

  const contentPromotion = {
    label: contentPromotionText,
    link: `${url}#promo`,
  };

  return !!contentPromotion.label && contentPromotion;
};

export const promoCreditSelector = (state) => {
  const creditData = getCreditData(state);
  const url = getBaseUrl(state);
  const payment = `${creditData?.monthPrice} ₽` || getLocalCurrencyStr(creditData?.minPayment);

  const label = creditData?.referenceTemplate
    .replace('<AnchorLink>', '')
    .replace('</AnchorLink>', '')
    .replace('₽', '')
    .replace('{{minPayment}}', `${payment}`);

  const promoCredit = {
    label,
    link: `${url}#payment`,
  };

  return !!creditData && promoCredit;
};

export const capacitySelector = (state) => {
  const types = getTypesParams(state);

  const capacityType = types.find((type) => type.title === 'Объем встроенной памяти');

  return capacityType?.list ?? [];
};

export const tShirtSelector = (state) => {
  const types = getTypesParams(state);

  const tShirtType = types.find((type) => type.title === 'Размер');

  return tShirtType?.list ?? [];
};

export const getTypes = createSelector(getTypesParams, (types) =>
  (types || []).map((type) => ({
    list: (type?.list || []).map((li, index) => ({
      id: index,
      isActive: li.isActive,
      link: li.href,
      title: li.title,
    })),
    title: type?.titleShort || type?.title,
  })),
);

export const isProductInBasketSelector = createSelector(
  getBodyData,
  getBasketProductIds,
  (device, basketProductIds) => !!basketProductIds.find((id) => id === `${device.productId}`),
);
export const isProductFavoriteSelector = (state) => getDetailsCommonData(state).isProductFavorite;

export const selectPromoTradeIn = createSelector(
  getTradeInSettings,
  getMainCollectionId,
  getTradeInEstimate,
  isTradeInEnabled,
  (tradeInSettings, collectionId, estimate, isTradeIn) => {
    if (!isTradeIn) {
      return null;
    }

    if (
      tradeInSettings?.categories.includes(collectionId) &&
      typeof tradeInSettings.productDiscount === 'string'
    ) {
      const [label] = tradeInSettings.productDiscount.split(' до') || '';
      const pureMaxDiscount = Number(tradeInSettings.maxDiscount.replace(/\s/g, ''));
      const price = estimate?.maxPrice ? estimate.maxPrice : pureMaxDiscount;

      return {
        label,
        link: '/shop/trade-in/',
        text: `до ${formatPrice(price)}₽`,
      };
    }

    return null;
  },
);

export const getDataForEventProductDetails = (state) => {
  const product = getBodyData(state);
  let memory = '-';
  // eslint-disable-next-line
  product?.characteristics?.detailed?.forEach((char) => {
    if (char.group === 'Память') {
      memory = char?.list[0]?.value;
    }
  });
  const activeColor = product?.colors?.find((color) => color?.isActive)?.title || '-';
  const variant = `${activeColor}_${memory}`;
  const category = product?.mainCollectionName?.toLowerCase();
  const impression = product?.impression;

  return {
    article: product?.article,
    brand: impression?.brand,
    category,
    name: impression?.name,
    price: impression?.price,
    variant,
  };
};

export const selectCalculatedPriceDiscount = (state) =>
  selectIsBundleActive(state) ? selectPromotionDiscount(state) : getPriceDiscount(state);

export const selectCalculatedOldPrice = (state) =>
  selectIsBundleActive(state) ? null : getOldPrice(state);

export const selectCalculatedPrice = (state) =>
  selectIsBundleActive(state) ? selectPromotionPrice(state) : getPrice(state);
export const selectBnpl = createSelector(getDetailsPageData, (pageData) =>
  Array.isArray(pageData?.bnpl) ? pageData.bnpl : [pageData?.bnpl],
);

export const selectIsInstallments = createSelector(
  getDetailsPageData,
  ({ creditDetails }) => !!creditDetails?.minPayment,
);

export const selectPromoBnplList = createSelector(selectBnpl, (installments) =>
  installments
    .filter((installment) => installment?.available && installment?.content?.isBnplFeatureShown)
    .map((installment) => ({
      primaryText: installment?.content?.shopBnplText,
      secondaryText: `${formatPrice(installment?.bnplPayment)} ₽ × ${
        installment?.bnplPaymentCount || DeviceInstallmentCount.BNPL
      }`,
      tooltipButtonHref: installment?.content?.fullInformationUrl,
      tooltipButtonText: PROMO_TOOLTIP_BUTTON_TEXT,
      tooltipMessage: installment?.content?.shopProductCartShortInfo,
    })),
);

export const selectIsBnplActive = (state) => selectPromoBnplList(state).length > 0;
export const selectProductDetailsSeoData = (state) => ({
  name: getPageTitle(state),
  images: getGalleryImages(state),
  price: selectCalculatedPrice(state),
  absoluteBaseUrl: getAbsoluteBaseUrl(state),
  count: getFeedbackCount(state),
  average: getFeedbackRating(state),
  brandName: getBrandName(state),
  reviews: getFeedbackPostsAdvanced(state),
  description: getSeoMetaDescription(state),
  sku: getProductArticle(state),
  isOutOfStock: getBodyData(state).isOutOfStock,
  isPreorder: getBodyData(state).isPreorder,
});
